// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import Tag from "@insite/mobius/Icons/Tag";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { wishListCardStyles } from "@steiner/Overrides/Components/WishListCard";
import Cart from "@steiner/XDL/Icons/Cart";
import Star from "@steiner/XDL/Icons/Star";
import StarX from "@steiner/XDL/Icons/StarX";
import Users from "@steiner/XDL/Icons/Users";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { color, ellipsis, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

wishListCardStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: minmax(240px,250px) minmax(0,1fr) minmax(165px,195px);
        grid-column-gap: 22px;
        grid-row-gap: 20px;
        width: 100%;
        justify-content: space-between;
        ${xScreen("lte", "md")} {
            grid-template-columns: minmax(317px, 1fr) minmax(0, 1fr);
        }
        ${xScreen("lte", "sm")} {
            grid-template-columns: 1fr;
        }
    `,
};

// Description Section Styles
wishListCardStyles.descriptionWrapper = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        > div {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        ${xScreen("lte", "md")} {
            ${({ imagesExist }: any) =>
                !imagesExist &&
                css`
                    grid-column: 1 / 3;
                `}
        }
    `,
};

wishListCardStyles.nameWrapper = {
    css: css`
        & > ${StyledButton} {
            font-weight: bold;
            ${TypographyStyle} {
                font-size: ${fontConfig.fontSize.regular}px;
                line-height: 19px;
            }
        }
    `,
};

wishListCardStyles.extendedInfoGridItem = {
    width: [12, 12, 7, 6, 6],
    align: "middle",
    css: css`
        align-items: center;
    `,
};

wishListCardStyles.lastUpdatedText = {
    color: "common.ui4",
    size: 14,
    lineHeight: "18px",
    italic: true,
};

wishListCardStyles.descriptionText = {
    size: 12,
    css: css`
        ${ellipsis(2)}
    `,
};

wishListCardStyles.actionFavoriteClickable = {
    css: css`
        width: auto;
        font-size: 12px;
        line-height: 13px;
        letter-spacing: 0;
        &:hover {
            color: ${color("common.ui9")};
            ${IconWrapper} {
                color: ${color("common.ui9")};
            }
        }
        ${({ favorite }: any) =>
            favorite &&
            css`
                color: ${color("primary")};
                ${IconWrapper} {
                    color: ${color("primary")};
                }
            `}
    `,
};

wishListCardStyles.favTagsWrapper = {
    css: css`
        display: flex;
        column-gap: 24px;
        align-items: center;
    `,
};

wishListCardStyles.starIcon = {
    src: Star,
    size: 12,
    css: css`
        margin-right: 6px;
    `,
};

wishListCardStyles.starXIcon = {
    src: StarX,
    size: 12,
    color: "primary",
    css: css`
        margin-right: 6px;
    `,
};

wishListCardStyles.viewTags = {
    size: 12,
    lineHeight: "13px",
};

wishListCardStyles.tagTooltip = {};

wishListCardStyles.tagIcon = {
    src: Tag,
    size: 12,
    css: css`
        margin-right: 6px;
    `,
};

// Image Section Styles
wishListCardStyles.imagesWrapper = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        display: flex;
        gap: 10px;
        justify-content: center;
        ${xScreen("lte", "md")} {
            justify-content: flex-start;
            grid-column: 2 / 3;
            grid-row: 1 / 2;
            &:empty {
                display: none;
            }
        }
        ${xScreen("lte", "sm")} {
            gap: 10px;
            grid-column: 1 / 2;
            grid-row: 2 / 3;
        }
    `,
};

wishListCardStyles.productImageLink = {
    css: css`
        display: flex;
        align-items: flex-start;
        margin: 0;
        height: fit-content;
    `,
};

wishListCardStyles.productImage = {
    mergeCss: true,
    width: "91px",
    height: "91px",
    css: css`
        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    `,
};

// actions Wrapper
wishListCardStyles.actionsWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 12px;
        grid-column: 3 / 4;
        grid-row: 1 / 2;
        ${xScreen("lte", "md")} {
            flex-direction: row;
            justify-content: space-between;
            flex-wrap: wrap;
            grid-column: 1 / 3;
            grid-row: 2 / 3;
        }
        ${xScreen("lte", "sm")} {
            grid-column: 1 / 2;
            grid-row: 3 / 4;
            ${({ imagesExist }: any) =>
                !imagesExist &&
                css`
                    grid-row: 2 / 3;
                `}
        }
        ${xScreen("lte", "xs")} {
            flex-direction: column;
        }
    `,
};

wishListCardStyles.detailIconWrapper = {
    css: css`
        line-height: 13px;
        > span {
            display: flex;
        }
    `,
};

wishListCardStyles.overflowMenu = {
    _width: 140,
    transitionDuration: "short",
    popoverPositionFunction: () => ({
        position: "absolute",
        top: "20px",
        left: "0",
    }),
    cssOverrides: {
        wrapper: css`
            width: auto;
            height: auto;
            & button[aria-expanded="true"] ${IconWrapper} svg {
                transform: rotate(180deg);
            }
        `,
        menu: css`
            padding: 4px 0;
            li:last-child button {
                padding-bottom: 8px;
                &::after {
                    display: none;
                }
            }
        `,
        menuItem: css`
            padding: 8px 12px 0;
            font-size: 12px;
            line-height: 17px;
            color: ${color("common.ui4")};
            font-weight: bold;
            &::after {
                content: "";
                display: block;
                border-bottom: 1px solid ${color("common.border")};
                width: 100%;
                margin-top: 8px;
            }
        `,
    },
};

wishListCardStyles.sharingStatus = {
    sharedIcon: {
        src: Users,
        size: 12,
        color: "primary",
        css: css`
            margin-right: 6px;
            opacity: 0.66;
        `,
    },
    statusText: {
        css: css`
            &:hover {
                ${IconWrapper} {
                    color: ${color("common.ui9")};
                }
            }
            ${TypographyStyle} {
                font-size: 12px;
                line-height: 13px;
                font-weight: ${fontConfig.weight.bold};
            }
        `,
    },
};

wishListCardStyles.actionAddToCartLink = {
    icon: {
        position: "left",
        iconProps: {
            src: Cart,
            size: 12,

            css: css`
                margin-right: 6px;
            `,
        },
    },
    typographyProps: { size: 12, weight: fontConfig.weight.bold },
};

wishListCardStyles.moreActions = {
    icon: {
        iconProps: {
            src: ChevronsUpDown,
            size: 12,
        },
    },
    typographyProps: {
        size: 12,
        weight: fontConfig.weight.bold,
    },
};

wishListCardStyles.wishListLinesCount = {
    size: 12,
    weight: fontConfig.weight.semiBold,
};
